import type { BreadcrumbItem, City, Faq, JsonLd, Service } from '@/types'

import { cities } from '@/constants/cities'
import { services } from '@/constants/services'
import { siteConfig } from '@/constants/site'

export const absoluteUrl = (path: string): string => (path === '/' ? `${siteConfig.url}/` : `${siteConfig.url}${path}`)

export const ORGANIZATION_ID = `${siteConfig.url}/#organization`

const WEBSITE_ID = `${siteConfig.url}/#website`

const servedCities = (): Array<JsonLd> => cities.map((city) => ({ '@type': 'City', name: city.name }))

export const organizationJsonLd = (): JsonLd => ({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': ORGANIZATION_ID,
    name: siteConfig.name,
    url: absoluteUrl('/'),
    description: siteConfig.description,
    telephone: siteConfig.phone.schema,
    email: siteConfig.email,
    image: siteConfig.ogImage,
    address: {
        '@type': 'PostalAddress',
        addressRegion: siteConfig.region,
        addressCountry: 'IN'
    },
    areaServed: servedCities(),
    knowsAbout: services.map((service) => service.name),
    hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: "IT hardware rental, AMC and refurbished laptops",
        itemListElement: services.map((service) => ({
            '@type': 'Offer',
            itemOffered: {
                '@type': 'Service',
                name: service.name,
                url: absoluteUrl(`/${service.slug}`)
            }
        }))
    }
})

export const websiteJsonLd = (): JsonLd => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: absoluteUrl('/'),
    name: siteConfig.name,
    inLanguage: 'en-IN',
    publisher: { '@id': ORGANIZATION_ID }
})

export const serviceJsonLd = (service: Service, city?: City): JsonLd => {
    const path = city ? `/${service.slug}/${city.slug}` : `/${service.slug}`

    return {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${absoluteUrl(path)}#service`,
        name: city ? service.city.heading(city) : service.heading,
        serviceType: service.name,
        description: city ? service.city.capsule(city) : service.capsule,
        url: absoluteUrl(path),
        provider: { '@id': ORGANIZATION_ID },
        areaServed: city ? { '@type': 'City', name: city.name } : servedCities()
    }
}

export const faqJsonLd = (faqs: Array<Faq>): JsonLd => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
        }
    }))
})

export const breadcrumbJsonLd = (items: Array<BreadcrumbItem>): JsonLd => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: absoluteUrl(item.path)
    }))
})
